import { useTranslation } from "react-i18next";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Kicker } from "@/components/site/shared";
import { PRODUCTS } from "@/data/site";

export default function ProductDetail() {
  const { t } = useTranslation();
  const { productId } = useParams();
  const product = PRODUCTS[productId];

  if (!product) return <Navigate to="/products" replace />;

  const apps = t(`products.${productId}.apps`, { returnObjects: true });

  return (
    <div data-testid={`product-detail-${productId}`}>
      <section className="border-b bg-secondary/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <Link to="/products" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors" data-testid="back-to-products">
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
            {t("productsPage.back")}
          </Link>
          <div className="mt-8 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <Kicker>{t(`categories.${product.category}.title`)}</Kicker>
              <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">{t(`products.${productId}.name`)}</h1>
              <p className="mt-4 text-lg text-primary font-medium">{t(`products.${productId}.tagline`)}</p>
              <p className="mt-5 text-muted-foreground leading-relaxed">{t(`products.${productId}.desc`)}</p>
              <Button asChild size="lg" className="mt-8 rounded-none font-semibold h-12 px-7" data-testid="product-request-quote">
                <Link to={`/contact?product=${productId}`}>
                  {t("productsPage.requestQuote")}
                  <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" />
                </Link>
              </Button>
            </div>
            <div className="aspect-[4/3] overflow-hidden border">
              <img src={product.image} alt={t(`products.${productId}.name`)} className="h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div data-testid="product-specs">
            <h2 className="font-heading text-2xl font-extrabold tracking-tight">{t("productsPage.specTitle")}</h2>
            <table className="mt-6 w-full border">
              <tbody>
                {product.specs.map((s) => (
                  <tr key={s.key} className="border-b last:border-b-0">
                    <th className="w-2/5 bg-secondary/60 px-4 py-3.5 text-start text-xs font-bold uppercase tracking-widest text-muted-foreground align-top">
                      {t(`productsPage.specs.${s.key}`)}
                    </th>
                    <td className="px-4 py-3.5 text-sm font-medium">{s.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div data-testid="product-applications">
            <h2 className="font-heading text-2xl font-extrabold tracking-tight">{t("productsPage.appsTitle")}</h2>
            <ul className="mt-6 space-y-3">
              {Array.isArray(apps) && apps.map((app, i) => (
                <li key={i} className="flex items-center gap-3 border bg-card px-4 py-3.5 text-sm font-medium">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                  {app}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
